import { deepCloneObject, deepEqualObject, updateObject } from '../utility';
import {
	sortByHeader as sortByHeaderHelper,
	toggleRowActionCell,
} from '../../components/UI/MDC/DataTable/_utilities';
import { checkValidity, updateHeaderActionCell, createQueue } from './_utility';
import { ACTION_TYPES, ACTION_FILTER_TYPES } from './_actionTypes';
import {
	IDENTIFIER_TYPES,
	VALIDATION_THRESHOLD,
	HEADERS_FOR_SEARCH,
} from '../../pages/Support/WorkQueue/WorkQueue/_constants';

const initialState = {
	actions: [],
	headers: [],
	loading: false,
	queue: [],
	rows: [],
	valid: true,
};

export const CELL_KEYS = {
	/**
	 * No action on request
	 * @type {string}
	 */
	NO_ACTION: 'No_Action',
};

/**
 * Case reducer.
 * Resets `rows` and `valid` of @param state
 *
 * @function
 * @param {Object} state current local state.
 * @returns {Object} updated local state.
 */
const clearData = state => {
	return updateObject(state, {
		rows: [], // PersonSearch DataTable configuration
		valid: false, // validity
	});
};

/**
 * Case reducer.
 * Updates `rows` of @param state by filtering `rows` of @param state by @param action property `payload.filterType`.
 *
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const filterRows = (state, action) => {
	const filteredRows = state.rows.filter(row => {
		switch (action.payload.filterType) {
			case ACTION_FILTER_TYPES.VALID_EXCLUDE:
				return Object.values(row.Cells).some(value => {
					return value.Validation.Key === 'false';
				});
			case ACTION_FILTER_TYPES.QUEUE_FILTER_PENDING:
				return row;
			case ACTION_FILTER_TYPES.QUEUE_FILTER_APPLICATION:
				return action.payload.application === 'All'
					? row
					: row.Cells.Application.Value ===
							action.payload.application;
			default:
				return true;
		}
	});
	const valid = filteredRows.every(row => checkValidity(row) === true);
	return updateObject(state, {
		rows: filteredRows,
		valid: valid,
	});
};

/**
 * Case reducer.
 * Updates `rows` of @param state with index search criteria of @param action property `payload`.
 * Conditionally updates `actions` of @param state if header action cell does not match state of `rows` action cells.
 *
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const indexRows = (state, action) => {
	const index = action.payload.toLowerCase();
	const arrHeaders = HEADERS_FOR_SEARCH.map(header =>
		header.replace(/ /g, '_')
	);
	let actions = state.actions;
	let valid = state.valid;

	// only display indexed rows
	let rowsCopy = deepCloneObject(state.rows);
	rowsCopy.forEach(row => {
		const indexed = Object.keys(row.Cells)
			.filter(key => arrHeaders.includes(key.replace(/ /g, '_')))
			.some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(index)
			);
		row.isDisplayed = indexed;
	});

	actions = updateHeaderActionCell(actions, rowsCopy, 0);
	valid = rowsCopy.every(row => checkValidity(row) === true);

	return updateObject(state, {
		actions: actions,
		rows: rowsCopy,
		valid: valid,
	});
};

/**
 * Case reducer.
 * Updates state with existing property values and addition of new properties.
 *
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const init = (state, action) => {
	return updateObject(state, action.payload);
};

/**
 * Case reducer.
 * Toggles root loading animation.
 *
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const loading = (state, action) =>
	updateObject(state, {
		loading: action.payload,
	});

/**
 * Case reducer.
 * Updates `rows` of @param state with @param action property `payload`. Resets sorting.
 *
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const setRows = (state, action) => {
	let actions = state.actions;
	let newRows = action.payload.rows;

	//if required, merge new rows with existing and remove row
	if (action.payload.merge) {
		let fixedIdentifierValues = [];

		//get the fixed row's identifier type values
		Object.keys(newRows[0].Cells).forEach(cell => {
			const identifierTypeKeys = Object.keys(IDENTIFIER_TYPES);
			if (identifierTypeKeys.includes(cell.toUpperCase())) {
				fixedIdentifierValues[cell] = {
					value: newRows[0].Cells[cell].Value,
					valid: newRows[0].Cells[cell].Validation.Key === 'true',
				};
			}
		});

		//merge the rows and remove duplicates
		newRows = [...newRows, ...state.rows].reduce((acc, currentValue) => {
			if (
				!acc.some(row => deepEqualObject(row.Cells, currentValue.Cells))
			) {
				acc.push(currentValue);
			}
			return acc;
		}, []);

		//remove previously invalid row
		newRows = newRows.filter(row => {
			const cell = row.Cells;
			return !Object.keys(fixedIdentifierValues).some(key => {
				let rowValue = cell[key].Value;
				rowValue = rowValue ? rowValue.toLowerCase().trim() : rowValue;
				const rowValidation = cell[key].Validation.Key.toLowerCase();
				let expectedValue = fixedIdentifierValues[key].value;
				expectedValue = expectedValue
					? expectedValue.toLowerCase()
					: expectedValue;
				const expectedValidation = fixedIdentifierValues[key].valid;
				return (
					rowValidation === 'false' &&
					expectedValidation &&
					rowValue === expectedValue
				);
			});
		});
	}

	// reset sorting as necessary
	let headersCopy = deepCloneObject(state.headers);
	if (headersCopy[0].props.sort < 1) {
		headersCopy[0].props.sort = 1;
		headersCopy = headersCopy.map((objHeader, index) => {
			if (index > 0 && objHeader.props) {
				objHeader.props.sort = null;
			}
			return objHeader;
		});
	}
	actions = updateHeaderActionCell(actions, newRows, 0);

	const valid = newRows.every(row => checkValidity(row) === true);

	return updateObject(state, {
		actions: actions,
		headers: headersCopy,
		rows: newRows, //valid rows will be filtered out of `rows` by `filterRows()` action
		valid: valid,
	});
};

/**
 * Case reducer.
 * Sorts `rows` of @param state against @param action properties `payload.header`, `payload.sortOrder`.
 * Invalid row indices always sorted to top.
 * Performs updates on both `rows` and `headers` state.
 *
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} conditionally updated local state.
 */
const sortByHeader = (state, action) => {
	let stateUpdated = state;
	let headersCopy = state.headers;

	// find header of interest
	let headerIndex = headersCopy.findIndex(
		objHeader => objHeader.Value === action.payload.header
	);
	let header = headerIndex > -1 ? headersCopy[headerIndex] : null;
	if (header && header.props) {
		const headerValue = header.Value.replace(/ /g, '_');
		header.props.sort = action.payload.sortOrder;
		// reset outstanding non-null sort values
		headersCopy = headersCopy.map((objHeader, index) => {
			if (index !== headerIndex && objHeader.props) {
				objHeader.props.sort = null;
			}
			return objHeader;
		});

		// update rows
		let rowsCopy = Array.isArray(state.rows) ? [...state.rows] : [];
		if (state.valid) {
			// validity not relevant
			rowsCopy = sortByHeaderHelper(
				rowsCopy,
				header.Value,
				header.props.sort
			);
		} else {
			// consider validity in sorting
			const ascending = header.props.sort > 0;
			rowsCopy.sort((firstEl, secondEl) => {
				if (
					firstEl.Cells &&
					secondEl.Cells &&
					firstEl.Cells[headerValue] &&
					secondEl.Cells[headerValue]
				) {
					const validFirstEl = checkValidity(firstEl);
					const validSecondEl = checkValidity(secondEl);
					if (!validFirstEl && validSecondEl) {
						return -1;
					} else if (validFirstEl && !validSecondEl) {
						return 1;
					} else {
						const valueFirstEl = firstEl.Cells[headerValue].Value
							? firstEl.Cells[headerValue].Value.toLowerCase()
							: '';
						const valueSecondEl = secondEl.Cells[headerValue].Value
							? secondEl.Cells[headerValue].Value.toLowerCase()
							: '';
						if (valueFirstEl < valueSecondEl) {
							return ascending ? -1 : 1;
						}
						if (valueFirstEl > valueSecondEl) {
							return ascending ? 1 : -1;
						}
					}
				}
				return 0;
			});
		}

		stateUpdated = updateObject(state, {
			headers: headersCopy,
			rows: rowsCopy,
		});
	}
	return stateUpdated;
};

/**
 * Case reducer.
 * Toggles presence of row in `requestees` of @param state when present, toggles row in `rows` of @param state when present.
 * Conditionally updates `actions` of @param state if header action cell does not match state of `rows` action cells.
 * Property `payload` of @param action possesses row of interest.
 *
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} conditionally updated local state.
 */
const toggleRow = (state, action) => {
	let actions = state.actions;
	let rowsCopy = state.rows;
	let queueCopy = deepCloneObject(state.queue);
	const row = action.payload;
	const indexRow = state.rows.findIndex(
		rowExisting => rowExisting.Key === row.Key
	);
	const indexQueue = queueCopy.findIndex(queue => queue.Key === row.Key);

	// if row exists in array of rows, toggle `Checked` state of row action cell
	if (indexRow > -1) {
		rowsCopy = toggleRowActionCell(state.rows, row, 0);
	}

	// if row exists in array of queues, update row selection; else, add to array
	if (indexQueue > -1) {
		queueCopy[indexQueue].isSelected = !queueCopy[indexQueue].isSelected;
	} else {
		queueCopy.push(createQueue(row));
	}

	actions = updateHeaderActionCell(actions, rowsCopy, 0);

	return updateObject(state, {
		actions: actions,
		queue: queueCopy,
		rows: rowsCopy,
	});
};

/**
 * Case reducer.
 * Toggles `requestees` and `rows` of @param state based on @action `payload' property.
 *
 * @function
 * @param {Object} state current local state.
 * @param {*} action dispatched action of interest. Expecting properties `e`, `index`.
 * @return {Object} updated local state.
 */
const toggleRows = (state, action) => {
	const e = action.payload.e;
	const index = action.payload.index;
	let queueCopy = deepCloneObject(state.queue);
	let rowsCopy = deepCloneObject(state.rows);
	let actionsCopy = deepCloneObject(state.actions);

	if (e && e.target && e.target.checked) {
		// filter unselected, valid rows
		const arrDiff = rowsCopy.filter(
			el => checkValidity(el) === true && !el.ActionCells[index].Checked
		);
		for (let i = 0; i < arrDiff.length; i++) {
			let el = arrDiff[i];
			// set unselected, valid rows as selected
			rowsCopy.find(row => row.Key === el.Key).ActionCells[
				index
			].Checked = true;

			// set pre-existing selections as selected; otherwise, add new selection
			const indexQueue = queueCopy.findIndex(
				queue => queue.Key === el.Key
			);
			if (indexQueue > -1) {
				queueCopy[indexQueue].isSelected = true;
			} else {
				queueCopy.push(createQueue(el));
			}
			if (
				queueCopy.filter(queue => queue.isSelected === true).length ===
				VALIDATION_THRESHOLD.MAX
			) {
				break;
			}
		}
		actionsCopy[index].Header.Checked = true;
	} else {
		rowsCopy.forEach(row => {
			row.ActionCells[index].Checked = false;
			const indexQueue = queueCopy.findIndex(
				queue => queue.Key === row.Key
			);
			if (indexQueue > -1) {
				queueCopy[indexQueue].isSelected = false;
			}
		});
		actionsCopy[index].Header.Checked = false;
	}
	return updateObject(state, {
		actions: actionsCopy,
		queue: queueCopy,
		rows: rowsCopy,
	});
};

/**
 * Work Queue Root Reducer
 * Resolves dispatched actions
 * @param {Object} state current local state.
 * @param {*} action dispatched action of interest. Properties `type`, `payload` required.
 */
const reducer = (state = initialState, action) => {
	switch (action.type) {
		case ACTION_TYPES.ADMIN_SEARCH_CLEAR_DATA:
			return clearData(state);
		case ACTION_TYPES.ADMIN_SEARCH_FILTER_ROWS:
			return filterRows(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_INIT:
			return init(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_LOADING:
			return loading(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_HEADER_SORT:
			return sortByHeader(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_ROWS:
			return setRows(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_ROWS_INDEX:
			return indexRows(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_ROW_TOGGLE:
			return toggleRow(state, action);
		case ACTION_TYPES.ADMIN_SEARCH_ROWS_TOGGLE:
			return toggleRows(state, action);
		default:
			return state;
	}
};

export default reducer;
